import React from 'react'
import Button from 'components/button'
import { Display, Moon, Sun } from 'components/icons'
import { useTheme } from 'hooks/useTheme'
import { ThemePreference } from 'helpers/theme'

const OPTIONS: { value: ThemePreference; label: string; Icon: React.FC<{ className?: string }> }[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'System', Icon: Display }
]

/**
 * Three-way appearance control.
 *
 * `System` is a first-class option rather than the absence of a choice, because
 * "follow my machine" is a real preference and people who set it want to see
 * that it is set. It is also the default, so Morta matches the rest of a
 * customer's desktop until they say otherwise.
 */
const Appearance: React.FC = () => {
  const { preference, setPreference } = useTheme()

  return (
    // Its edges line up with the menu rows' hover backgrounds.
    <div className="pb-1">
      <div
        role="radiogroup"
        aria-label="Appearance"
        className="flex gap-0.5 p-0.5 rounded-md bg-surface-sunken border border-divider"
      >
        {OPTIONS.map(({ value, label, Icon }) => {
          const selected = preference === value

          return (
            <Button
              key={value}
              role="radio"
              aria-checked={selected}
              data-testid={`theme-option-${value}`}
              internalType="transparent"
              onClick={() => setPreference(value)}
              className={
                selected
                  ? 'flex-1 h-7 px-1.5 text-xs bg-surface text-body border-divider'
                  : 'flex-1 h-7 px-1.5 text-xs text-muted'
              }
            >
              <Icon className="shrink-0" />
              {label}
            </Button>
          )
        })}
      </div>
    </div>
  )
}

export default Appearance
